import { AfterViewChecked, Component, forwardRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ErrorMessages, Permissions, SAVE_DATA_ERROR } from '../../common/constants';
import { IMPORT_THINGS_V2, THING_DEFINITIONS } from '../../common/endpoints';
import { Location, ProductModel, ProductModelCategory, ThingDefinition, ThingInventoryManagementType } from '../../model';
import { AuthenticationService } from '../../service/authentication.service';
import { DefaultPropertySettingsService, DefaultPropertySettingsType } from '../../service/default-property-settings.service';
import { FlatTreeNode } from '../../service/flat-tree.service';
import { FormCheckerService } from '../../service/form-checker.service';
import { HttpService } from '../../service/http.service';
import { ProductModelCategoryService } from '../../service/product-model-category.service';
import { ProductModelService } from '../../service/product-model.service';
import { AbstractContextService } from '../../shared/class/abstract-context-service.class';
import { FormChecked } from '../../shared/interface/form-checked.interface';
import { HttpUtility } from '../../utility';
import { ErrorUtility } from '../../utility/error-utility';

@Component({
    selector: 'register-things-widget-v2',
    template: require('./register-things-v2.component.html'),
    styles: [`
        frame-custom-content mat-checkbox ::ng-deep label {
            white-space: unset;
        }

        .mat-select-group-option {
            font-weight: 600;
        }

        label.required:after {
            content:" *";
        }
    `]
})
export class RegisterThingsV2Component implements OnInit, FormChecked, OnDestroy, AfterViewChecked {

    @Input() title: string;

    @Input() width: string;

    @Input() description: string;

    @ViewChild('importForm') importForm: NgForm;

    showFields: boolean;
    thingDefinitions: ThingDefinition[] = [];
    error: string;
    readPermission: boolean;
    csvLoaded: boolean;
    importInProgress: boolean;
    multipleRegister: boolean = true;
    serialNumber: string;
    name: string;
    assetId: string;
    location: Location;
    initFileInput: boolean;
    thingInventoryManagement: ThingInventoryManagementType;
    visibleProductModels: ProductModel[]
    productModelFlatTree: FlatTreeNode[];
    nameUnique: boolean;
    serialNumberUnique: boolean;
    nameMandatory: boolean;
    serialNumberMandatory: boolean;
    showConfigurationError: boolean;

    private csv: File;
    private fileInputs: any;
    private allProductModels: ProductModel[] = [];
    private productModelCategories: ProductModelCategory[];

    static REGISTER_THINGS_V2_FORM_KEY = 'REGISTER_THINGS_V2_FORM_KEY';

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) private authService: AuthenticationService,
        @Inject(forwardRef(() => FormCheckerService)) private formCheckerService: FormCheckerService,
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
        @Inject(forwardRef(() => HttpUtility)) private httpUtility: HttpUtility,
        @Inject(forwardRef(() => ProductModelService)) private productModelService: ProductModelService,
        @Inject(forwardRef(() => ProductModelCategoryService)) private productModelCategoryService: ProductModelCategoryService,
        @Inject(forwardRef(() => DefaultPropertySettingsService)) private defaultPropertySettingsService: DefaultPropertySettingsService
    ) { }

    loadCsv(event): void {
        const files: FileList = <any>(event.srcElement || event.target).files;
        if (files.length > 0) {
            this.csvLoaded = true;
            this.csv = files.item(0);
        } else {
            this.csvLoaded = false;
            this.csv = null;
        }
    }

    ngOnInit(): void {
        this.title = this.title || 'registerThingsTitle';
        this.readPermission = this.authService.hasPermission(Permissions.REGISTER_THING);
        this.formCheckerService.registerForm(this);
        this.location = this.contextService.getCurrentLocation();
        this.thingInventoryManagement = this.authService.getThingInventoryManagement();
        Promise.all(this.getPromises()).then(() => {
            this.filterVisibleProductModels(null);
            this.buildProductModelFlatTree();
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
        this.checkUniqueProperties();
    }

    private getPromises(): any[] {
        let promises = [];
        promises.push(this.getThingDefinitions().then(definitions => this.thingDefinitions = definitions));
        if (this.thingInventoryManagement == ThingInventoryManagementType.BY_MODEL || this.thingInventoryManagement == ThingInventoryManagementType.BY_THING_DEFINITION_AND_MODEL) {
            promises.push(this.productModelService.getRecursivelyAllProductModels().then(productModels => this.allProductModels = productModels.filter(pm => pm.thingDefinitionId)));
            promises.push(this.productModelCategoryService.getRecursivelyAllProductModelCategories().then(productModelCategories => this.productModelCategories = productModelCategories));
        }
        return promises;
    }

    ngAfterViewChecked(): void {
        if (!this.initFileInput) {
            const opt = {
                buttonBefore: true,
                disabled: false,
                btnClass: "btn-default",
                dragdrop: false
            };
            this.fileInputs = $(':file') as any;
            this.fileInputs.filestyle(opt);
            this.initFileInput = true;
        }
    }

    import(): void {
        this.importInProgress = true;
        const context = 'Bulk import things';
        const data = new FormData();
        const rawValues = this.importForm.form.getRawValue();
        if (this.multipleRegister) {
            data.set('file', this.csv, this.csv.name);
        } else {
            let fileString;
            const mandatoryPropertyColumns = rawValues.properties ? Object.keys(rawValues.properties).map(key => { return ('properties.' + key) }) : [];
            const mandatoryPropertyValues: any[] = rawValues.properties ? Object.values(rawValues.properties) : [];
            let columns = ['serialNumber', 'name'];
            let values = [this.serialNumber, this.name];
            if (this.location) {
                columns.push('mapping.assetId');
                values.push(this.assetId);
            }
            let thingDefId = rawValues.thingDefinitionId;
            if (thingDefId) {
                const thingDef = this.thingDefinitions.find(td => td.id == thingDefId);
                columns.push('thingDefinitionName');
                values.push(thingDef.name);
            }
            let productModelId = rawValues.productModelId;
            if (productModelId) {
                const productModel = this.allProductModels.find(pm => pm.id == productModelId);
                columns.push('productModelName');
                values.push(productModel.name);
            }
            fileString = (columns.concat(mandatoryPropertyColumns)).join(',') + '\n' + (values.concat(mandatoryPropertyValues)).join(',');
            const file = new Blob([fileString]);
            data.set('file', file, 'single-thing.csv');
        }
        if (this.location) {
            data.set('locationId', this.location.id);
        }
        firstValueFrom(this.httpService.post(IMPORT_THINGS_V2, data, null, context))
            .then(() => {
                this.error = null;
                this.importInProgress = false;
                this.csv = null;
                this.csvLoaded = false;
                this.fileInputs.filestyle('clear');
                this.showFields = false;
                this.serialNumber = "";
                this.assetId = "";
                this.name = "";
            })
            .catch((err) => {
                this.error = ErrorUtility.getMessage(err, SAVE_DATA_ERROR);
                this.csv = null;
                this.csvLoaded = false;
                this.fileInputs.filestyle('clear');
                this.importInProgress = false;
            });
    }

    getFormKey(): string {
        return RegisterThingsV2Component.REGISTER_THINGS_V2_FORM_KEY;
    }

    isDirty(): boolean {
        if (this.importForm) {
            const rawValues = this.importForm.form.getRawValue();
            return (rawValues.thingDefinitionId || rawValues.productModelId || rawValues.serialNumber || rawValues.name);
        }
        return false;
    }

    resetStatus(): void {
        this.error = null;
        this.showFields = false;
        this.importInProgress = false;
        this.fileInputs.filestyle('clear');
    }

    ngOnDestroy(): void {
        this.formCheckerService.unregisterForm(this.getFormKey());
    }

    getThingDefinitions(): Promise<ThingDefinition[]> {
        return firstValueFrom(this.httpService.get<ThingDefinition[]>(THING_DEFINITIONS));
    }

    downloadExampleCsv(): void {
        let columnValuesMap: { [column: string]: string[] } = {
            'serialNumber': ['SerialNumber1', 'SerialNumber2'],
            'name': ['Name1', 'Name2'],
            'gpsPosition': ['"1.12345,2.12345"', '"10.12345,11.12345"'],
            'properties.<property_name>': ['Prop1', 'Prop2'],
            'mapping.path': ['Path1', 'Path2'],
            'mapping.assetId': ['AssetId1', 'AssetId2']
        }
        if (this.authService.hasPermission(Permissions.WRITE_CERTIFICATE)) {
            columnValuesMap['sharedKey'] = ['SharedKey1', 'SharedKey2'];
            columnValuesMap['publicKey'] = ['PublicKey1', 'PublicKey2'];
        }
        if (this.thingInventoryManagement == ThingInventoryManagementType.BY_THING_DEFINITION) {
            columnValuesMap['thingDefinitionName'] = ['ThingDef1', 'ThingDef2'];
        } else if (this.thingInventoryManagement == ThingInventoryManagementType.BY_MODEL) {
            columnValuesMap['productModelName'] = ['Model1', 'Model2'];
            columnValuesMap['productModelPartName'] = ['ModelPart1', 'ModelPart2'];
        } else {
            columnValuesMap['thingDefinitionName'] = ['ThingDef1', 'ThingDef2'];
            columnValuesMap['productModelName'] = ['Model1', 'Model2'];
            columnValuesMap['productModelPartName'] = ['ModelPart1', 'ModelPart2'];
        }
        columnValuesMap['parentThingSerialNumber'] = ['ParentThingSerialNumber1', 'ParentThingSerialNumber2'];
        columnValuesMap['parentThingName'] = ['ParentThingName1', 'ParentThingName2'];
        const blob = new Blob([this.buildCsv(columnValuesMap)]);
        this.httpUtility.wrapFileAndDownload({ file: blob, fileName: "csvExample.csv" });
    }

    private buildCsv(columnValuesMap: { [column: string]: string[] }): string {
        let columnValues = [];
        let firstRowValues = [];
        let secondRowValues = [];
        Object.keys(columnValuesMap).forEach(key => {
            columnValues.push(key);
            firstRowValues.push(columnValuesMap[key][0]);
            secondRowValues.push(columnValuesMap[key][1]);
        });
        return columnValues.join(',') + '\n' + firstRowValues.join(',') + '\n' + secondRowValues.join(',');
    }

    filterVisibleProductModels(thingDefId: string): void {
        if ([ThingInventoryManagementType.BY_THING_DEFINITION_AND_MODEL].includes(this.thingInventoryManagement)) {
            this.importForm?.form?.controls['productModelId']?.reset();
            if (thingDefId) {
                this.visibleProductModels = this.allProductModels.filter(pm => pm.thingDefinitionId == thingDefId);
            } else {
                this.visibleProductModels = [];
            }
        } else if ([ThingInventoryManagementType.BY_MODEL].includes(this.thingInventoryManagement)) {
            this.visibleProductModels = this.allProductModels;
        }
    }

    thingDefinitionChanged(thingDefinitionId: string): void {
        this.filterVisibleProductModels(thingDefinitionId);
        this.buildProductModelFlatTree();
    }

    private buildProductModelFlatTree(): void {
        this.productModelFlatTree = this.productModelCategoryService.buildProductModelCategoryWithProductModelFlatTree(this.visibleProductModels, this.productModelCategories);
    }

    getCurrentThingDefinitionId(): string {
        const rawValues = this.importForm?.form?.getRawValue();
        if (rawValues?.productModelId) {
            return this.allProductModels?.find(pm => pm.id == rawValues.productModelId)?.thingDefinitionId;
        } else {
            return rawValues?.thingDefinitionId;
        }
    }

    private checkUniqueProperties(): void {
        this.defaultPropertySettingsService.getTenantDefaultPropertySettingsByType(DefaultPropertySettingsType.THING).forEach(prop => {
            if (prop.name == 'name') {
                this.nameUnique = prop.unique;
                this.nameMandatory = prop.mandatory;
            }
            if (prop.name == 'serialNumber') {
                this.serialNumberUnique = prop.unique;
                this.serialNumberMandatory = prop.mandatory;
            }
        });
        if (!this.nameUnique && !this.serialNumberUnique) {
            this.showConfigurationError = true;
        }
    }

    isSaveDisabled(): boolean {
        return (this.multipleRegister && !this.csvLoaded) || (!this.multipleRegister && !this.importForm?.valid) || this.importInProgress || this.showConfigurationError;
    }
}